import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button, Input, Popconfirm, Table, TableProps, message } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { PiPlus } from "react-icons/pi";
import { CiTrash } from "react-icons/ci";

import SectionTitle from "../../Components/SectionTitle";
import DashPagination from "../../Components/DashPagination";
import DashModal from "../../Components/DashModal";
import AddNewFeature from "./Components/AddNewFeature";

import { getCounter } from "../../lib/functions";
import { deleteFeature, getFeatures } from "./action";
import EditFeature from "./Components/EditFeature";

const { Search } = Input;

type Props = {
  addNewFeature?: boolean;
  editFeature?: boolean;
};

interface TableDataType {
  id: string;
  en: string;
  ar: string;
  fr: string;
}

const Features: React.FC<Props> = ({ addNewFeature, editFeature }) => {
  const [data, setData] = useState<TableDataType[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [query, setQuery] = useState("");
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchCount = useCallback(async () => {
    try {
      const count = await getCounter("BettingWebsiteFeature");
      setTotal(count || 0);
    } catch (error) {
      message.error("Failed to fetch count");
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const features = await getFeatures(pageSize, page, query);
      const formatted = features.map((ft) => ({
        id: ft.id,
        en: ft.Feature.En,
        ar: ft.Feature.Ar,
        fr: ft.Feature.Fr,
      }));
      setData(formatted);
    } catch (error) {
      message.error("Failed to fetch features");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, query]);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (id: string) => {
    try {
      await deleteFeature(id);
      message.success("Feature deleted");
      fetchData();
      fetchCount();
    } catch (error) {
      message.error("Failed to delete feature");
    }
  };

  const columns: TableProps<TableDataType>["columns"] = useMemo(
    () => [
      {
        title: "English",
        dataIndex: "en",
        key: "en",
      },
      {
        title: "Arabic",
        dataIndex: "ar",
        key: "ar",
      },
      {
        title: "French",
        dataIndex: "fr",
        key: "fr",
      },
      {
        title: "Action",
        key: "action",
        render: (_, record) => (
          <div className="flex gap-2">
            <Link
              to={`/features/edit/${record.id}?page=${
                page + 1
              }&pageSize=${pageSize}`}
            >
              <Button type="link" icon={<EditOutlined />} />
            </Link>
            <Popconfirm
              title="Are you sure you want to delete this feature?"
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="dashed" danger icon={<CiTrash />} />
            </Popconfirm>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-5">
      <SectionTitle title="Features" />

      <div className="flex justify-between items-center">
        <Link to={`/features/add?page=${page + 1}&pageSize=${pageSize}`}>
          <Button type="primary" icon={<PiPlus />}>
            Add New Feature
          </Button>
        </Link>
        <Search
          allowClear
          placeholder="Search feature..."
          className="w-1/5"
          onSearch={(value) => setQuery(value)}
        />
      </div>

      <Table<TableDataType>
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={false}
      />

      <DashPagination
        page={page}
        setPage={setPage}
        totalNumber={total}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />

      {addNewFeature && (
        <DashModal
          title="Add New Feature"
          width={800}
          cancleLink={`/features?page=${page + 1}&pageSize=${pageSize}`}
        >
          <AddNewFeature fetchData={fetchData} />
        </DashModal>
      )}

      {editFeature && (
        <DashModal
          title="Edit Feature"
          width={800}
          cancleLink={`/features?page=${page + 1}&pageSize=${pageSize}`}
        >
          <EditFeature fetchData={fetchData} />
        </DashModal>
      )}
    </div>
  );
};

export default Features;
